import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import Scrollbars from "react-custom-scrollbars";
import { BottomSheet } from "react-spring-bottom-sheet";
import { isWindows } from "react-device-detect";
import cn from "clsx";
import { useIsRTL } from "@/hooks";
import SlideBottomToTop from "@/components/slide-bottom-to-top";
import useIsSmallScreen from "@/hooks/useIsSmallScreen";
import "react-spring-bottom-sheet/dist/style.css";
import "./style.scss";

import YellowCheckmark from "@/images/yellow_checkmark.svg";
import BlackCheckmark from "@/images/black_checkmark.svg";
import { useTranslation } from "react-i18next";

type DeliveryOption = {
  isExpress: boolean;
  sortOrder: number;
  stateName: string;
  stateId: number;
  title: string;
};

type DeliveryDropdownProps = {
  isOpen: boolean;
  onClose: () => void;
  data: DeliveryOption[];
  onOptionClick: (option: number) => void;
  selectedOption: DeliveryOption;
};

const DeliveryDropdown: FC<DeliveryDropdownProps> = ({
  isOpen,
  onClose,
  data,
  onOptionClick,
  selectedOption
}) => {
  const [searchValue, setSearchValue] = useState("");
  const { t } = useTranslation("productDetails");
  const wrapperRef = useRef(null);
  const isRTL = useIsRTL();
  const { isSmallScreen } = useIsSmallScreen();

  const sortedData = useMemo(() => {
    if (!data || !selectedOption) return data;

    return [...data].sort((a, b) => {
      if (a.stateId === selectedOption.stateId) return -1;
      if (b.stateId === selectedOption.stateId) return 1;
      return a.sortOrder - b.sortOrder;
    });
  }, [data, selectedOption]);

  const handleChange = option => {
    onOptionClick(option);
    onClose();
  };

  useEffect(() => {
    !isSmallScreen && handleScroll();

    return () => {
      !isSmallScreen && closeDropdown();
    };
  }, [isSmallScreen]);

  useEffect(() => {
    !isOpen && setSearchValue("");
  }, [isOpen]);

  const handleScroll = () => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", closeDropdown);
  };

  const handleClickOutside = e => {
    const refWrapper = wrapperRef && !wrapperRef.current?.contains(e.target);

    if (!isOpen && refWrapper) {
      e.stopPropagation();
      onClose();
    }
  };

  const closeDropdown = () => {
    if (isOpen) {
      onClose();
    }
  };

  return isSmallScreen ? (
    <BottomSheet
      open={isOpen}
      onDismiss={onClose}
      data-body-scroll-lock-ignore="true"
      className={cn("delivery_dropdown_mob timeline_dropdown", {
        arabic: isRTL
      })}
      scrollLocking
      blocking
      maxHeight={300}
    >
      <div className="state-search-wrapper">
        <input
          type="text"
          placeholder={t("search")}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value.toLocaleLowerCase())}
          aria-label={`Search for state`}
        />
      </div>
      <span
        className="mCrossIcon"
        onClick={e => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div className="bottom_dropdown_wrapper">
        <Scrollbars autoHeight autoHeightMax={225} style={{ height: "225px" }}>
          <SlideBottomToTop>
            <ul className="dropdown_list" onClick={e => e.preventDefault()}>
              {sortedData
                ?.filter(option =>
                  option.stateName.toLocaleLowerCase().includes(searchValue)
                )
                .map((option, index) => (
                  <li
                    key={`${option?.stateId}-${index}`}
                    onClick={e => {
                      e.stopPropagation();
                      handleChange(option);
                    }}
                  >
                    <p>
                      {`${option?.stateName}`}
                      {option?.stateId === selectedOption?.stateId && (
                        <img src={YellowCheckmark} alt="yellow checkmark" />
                      )}
                    </p>
                  </li>
                ))}
            </ul>
          </SlideBottomToTop>
        </Scrollbars>
      </div>
    </BottomSheet>
  ) : isOpen ? (
    <div
      className={cn("delivery_dropdown_web", {
        arabic: isRTL,
        windows: isWindows
      })}
      ref={wrapperRef}
    >
      <div className="state-search-wrapper">
        <input
          type="text"
          placeholder={t("search")}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value.toLocaleLowerCase())}
          aria-label={`Search for state`}
        />
      </div>
      <Scrollbars
        universal
        autoHeight
        autoHeightMin={25}
        autoHeightMax={160}
        renderTrackHorizontal={props => (
          <div
            {...props}
            style={{ display: "none" }}
            className="track-horizontal"
          />
        )}
        renderTrackVertical={props => (
          <div
            {...props}
            style={{ display: "none" }}
            className="track-vertical"
          />
        )}
        className="delivery_scrollbar"
      >
        <ul className="dropdown_state_content_container">
          {sortedData
            ?.filter(option =>
              option.stateName.toLocaleLowerCase().includes(searchValue)
            )
            .map((option, index) => (
              <li
                className={cn("delivery_state_option", {
                  selected_delivery_option:
                    option?.stateId === selectedOption?.stateId
                })}
                key={`${option?.stateId}-${index}`}
                onClick={() => {
                  handleChange(option);
                }}
              >
                {option?.stateName}
                {option?.stateId === selectedOption?.stateId && (
                  <img src={BlackCheckmark} alt="black checkmark" />
                )}
              </li>
            ))}
        </ul>
      </Scrollbars>
    </div>
  ) : null;
};

export default DeliveryDropdown;
